import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { PageHeader, Panel, StatusBadge, Table, T, EmptyState, Skeleton, money, shortDate, DeleteButton, MobileCards } from "@/components/admin/ui";

const STATUS_FILTERS = [
  ["", "Toutes"], ["issued", "Émises"], ["sent", "Envoyées"], ["partially_paid", "Partielles"],
  ["paid", "Payées"], ["overdue", "En retard"], ["draft", "Brouillons"], ["cancelled", "Annulées"],
];

export default function AdminInvoices() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [paidFilter, setPaidFilter] = useState("");
  const navigate = useNavigate();

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    if (paidFilter) params.set("paid", paidFilter);
    adminApi.get(`/invoices?${params}`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [status, paidFilter]);
  useEffect(() => { adminApi.get("/billing/summary").then((r) => setSummary(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="admin-invoices">
      <PageHeader title="Factures" subtitle="Émission, paiements, relances et Swiss QR Bill"
        actions={<Link to="/admin/app/invoices/new" data-testid="invoice-new-link" className={T.btnPrimary}>
          <Plus className="h-4 w-4" strokeWidth={2} /> Nouvelle facture
        </Link>} />

      {summary && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="billing-kpis">
          {[["Facturé", summary.billed_total, ""], ["En attente", summary.outstanding, summary.outstanding_count > 0 ? `${summary.outstanding_count} facture${summary.outstanding_count > 1 ? "s" : ""}` : ""],
            ["En retard", summary.overdue, summary.overdue_count > 0 ? `${summary.overdue_count} à relancer` : ""], ["Payé ce mois", summary.paid_this_month, ""]].map(([label, value, hint]) => (
            <div key={label} className="rounded-2xl border border-ink/[0.07] bg-white p-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate/60">{label}</p>
              <p className={`mt-1 font-mono text-xl font-semibold ${label === "En retard" && value > 0 ? "text-red-600" : "text-ink"}`}>{money(value)}</p>
              {hint && <p className="mt-0.5 text-xs text-slate">{hint}</p>}
            </div>
          ))}
        </div>
      )}

      <Panel padded={false}
        title={<span className="flex w-full flex-wrap items-center gap-2.5">
          <span className="relative min-w-0 flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate/50" strokeWidth={2} />
            <input value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              onBlur={load}
              placeholder="N°, client, e-mail…" data-testid="invoices-search"
              className="h-10 w-full rounded-xl border border-ink/10 bg-cloud pl-9 pr-3 text-sm text-ink outline-none transition-colors placeholder:text-slate/60 focus:border-miles sm:w-64" />
          </span>
          <select value={paidFilter} onChange={(e) => setPaidFilter(e.target.value)} data-testid="invoices-paid-filter"
            className="h-10 rounded-xl border border-ink/10 bg-cloud px-3 text-sm text-ink outline-none focus:border-miles">
            <option value="">Payées + impayées</option>
            <option value="unpaid">Impayées</option>
            <option value="paid">Payées / soldées</option>
          </select>
        </span>}>
        <div className="flex gap-1.5 overflow-auto px-5 pt-4" data-testid="invoices-status-filters">
          {STATUS_FILTERS.map(([value, label]) => (
            <button key={value} onClick={() => setStatus(value)} data-testid={`filter-${value || "all"}`}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                status === value ? "bg-miles text-white" : "text-slate hover:bg-ink/[0.04]"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="mt-2">
          {!data ? <Skeleton rows={6} /> : data.invoices.length === 0 ? (
            <EmptyState title="Aucune facture" hint="Créez votre première facture — depuis une réservation ou de zéro."
              action={<Link to="/admin/app/invoices/new" className={T.btnSecondary}>Créer une facture</Link>} testId="invoices-empty" />
          ) : (
            <>
            <div className="hidden md:block">
            <Table head={["N°", "Client", "Émise le", "Échéance", "Montant", "Solde", "Statut", ""]}>
              {data.invoices.map((inv) => (
                <tr key={inv.id} className="cursor-pointer transition-colors hover:bg-cloud/60"
                  onClick={() => navigate(`/admin/app/invoices/${inv.id}`)} data-testid={`invoice-row-${inv.number || inv.id}`}>
                  <td className="font-mono text-[13px] font-semibold text-ink">{inv.number || "Brouillon"}</td>
                  <td>
                    <p className="font-medium text-ink">{inv.customer?.name}</p>
                    <p className="text-xs text-slate">{inv.customer?.email}</p>
                  </td>
                  <td className="text-slate">{shortDate(inv.issued_at) || "—"}</td>
                  <td className={inv.status === "overdue" ? "font-medium text-red-600" : "text-slate"}>{shortDate(inv.due_date) || "—"}</td>
                  <td className="font-mono text-[13px] text-ink">{money(inv.total)}</td>
                  <td className={`font-mono text-[13px] ${inv.balance > 0 ? "font-semibold text-ink" : "text-slate"}`}>{money(inv.balance)}</td>
                  <td><StatusBadge status={inv.status} /></td>
                  <td className="text-right" onClick={(e) => e.stopPropagation()}>
                    {/* discipline comptable : seules brouillons/annulées sont supprimables */}
                    {["draft", "cancelled"].includes(inv.status) && (
                      <DeleteButton small testId={`invoice-delete-${inv.number || inv.id}`}
                        onConfirm={() => adminApi.delete(`/invoices/${inv.id}`)}
                        onDeleted={() => setData((d) => ({ ...d, invoices: d.invoices.filter((x) => x.id !== inv.id), total: d.total - 1 }))} />
                    )}
                  </td>
                </tr>
              ))}
            </Table>
            </div>
            <MobileCards testId="invoices-cards-mobile">
              {data.invoices.map((inv) => (
                <div key={inv.id} onClick={() => navigate(`/admin/app/invoices/${inv.id}`)}
                  data-testid={`invoice-card-${inv.number || inv.id}`}
                  className="rounded-2xl border border-ink/[0.07] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-transform active:scale-[0.99]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[13px] font-semibold text-ink">{inv.number || "Brouillon"}</span>
                    <StatusBadge status={inv.status} />
                  </div>
                  <p className="mt-1.5 font-medium text-ink">{inv.customer?.name}</p>
                  {inv.customer?.email && <p className="truncate text-xs text-slate">{inv.customer.email}</p>}
                  <div className="mt-3 flex items-end justify-between">
                    <div className="text-[11px] text-slate">
                      <p>Émise {shortDate(inv.issued_at) || "—"}</p>
                      <p className={inv.status === "overdue" ? "font-semibold text-red-600" : ""}>Échéance {shortDate(inv.due_date) || "—"}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-[15px] font-semibold text-ink">{money(inv.total)}</p>
                      {inv.balance > 0 && <p className="font-mono text-[11px] font-semibold text-red-600">Solde {money(inv.balance)}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </MobileCards>
            </>
          )}
        </div>
      </Panel>
    </div>
  );
}
